import { useQuery } from "@tanstack/react-query";
import { History, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";

interface Version {
  num: string;
  current: boolean;
  author: string;
  when: string;
  msg: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  docTitle?: string;
  docId?: string;
  versions?: Version[];
}

// Fallback to mock data when the API endpoint isn't available yet.
const MOCK_VERSIONS: Version[] = [
  {
    num: "v.42",
    current: true,
    author: "@ortunoj",
    when: "hace 2 días",
    msg: "Actualizar comando wrangler deploy",
  },
  {
    num: "v.41",
    current: false,
    author: "@lucia",
    when: "hace 5 días",
    msg: "Añadir paso de configuración TLS",
  },
  {
    num: "v.40",
    current: false,
    author: "@manuel",
    when: "hace 2 sem.",
    msg: "Restructurar troubleshooting",
  },
  {
    num: "v.39",
    current: false,
    author: "github-app",
    when: "hace 3 sem.",
    msg: "Sync desde main · sha:abc1234",
  },
];

/**
 * B3.6 — Version drawer.
 *
 * Right-anchored drawer with a timeline of versions. Selecting a version
 * enables the "Compare v.X ↔ v.Y" button (placeholder until the diff
 * endpoint lands in B5).
 */
export function VersionDrawer({ open, onClose, docTitle, docId, versions: forced }: Props) {
  const [compareWith, setCompareWith] = useState<string | null>(null);

  const live = useQuery({
    queryKey: ["doc-versions", docId],
    queryFn: () => (docId ? api.listDocVersions(docId) : Promise.resolve({ items: [] })),
    enabled: open && !!docId,
    staleTime: 60_000,
  });

  // Build display versions from live API, fallback to mock if unavailable
  const versions: Version[] = forced
    ? forced
    : (() => {
        const items = live.data?.items ?? [];
        if (items.length === 0) return MOCK_VERSIONS;
        return items.map((v, idx) => ({
          num: `v.${items.length - idx}`,
          current: idx === 0,
          author: v.git_sha ? `git ${v.git_sha.slice(0, 7)}` : v.source_type,
          when: formatRelative(v.committed_at ?? v.created_at),
          msg: v.git_branch ? `branch ${v.git_branch}` : v.source_type,
        }));
      })();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        data-testid="version-drawer-backdrop"
        className="fixed inset-0 z-30 bg-black/30"
        onClick={onClose}
        aria-label="Cerrar"
      />
      <aside
        data-testid="version-drawer"
        aria-label="Historial de versiones"
        className="fixed right-0 top-0 z-40 flex h-full w-96 flex-col border-l border-(--border-default) bg-(--bg-canvas) shadow-(--elev-3)"
      >
        <header className="flex items-center justify-between border-b border-(--border-subtle) px-4 py-3">
          <div className="flex items-center gap-2">
            <History size={14} />
            <h3 className="text-sm font-semibold">Historial</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="version-drawer-close"
            aria-label="Cerrar"
            className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {docTitle && (
            <p className="mb-3 text-xs text-(--text-tertiary)">
              {versions.length} versiones de <strong>{docTitle}</strong>
            </p>
          )}
          <ol className="space-y-2">
            {versions.map((v) => {
              const selected = compareWith === v.num;
              return (
                <li key={v.num}>
                  <button
                    type="button"
                    onClick={() => !v.current && setCompareWith(v.num)}
                    data-testid={`version-row-${v.num}`}
                    className={`flex w-full gap-3 rounded-md border px-3 py-2 text-left transition ${
                      v.current
                        ? "border-(--accent-500) bg-(--accent-50)"
                        : selected
                          ? "border-(--accent-300) bg-(--bg-active)"
                          : "border-(--border-subtle) hover:bg-(--bg-hover)"
                    }`}
                  >
                    <span
                      className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
                        v.current ? "bg-(--accent-500)" : "bg-(--text-tertiary)"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold">{v.num}</span>
                        {v.current && (
                          <span className="rounded-full bg-(--accent-100) px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-(--accent-700)">
                            actual
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-(--text-primary) truncate">{v.msg}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-(--text-tertiary)">
                        {v.author} · {v.when}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        <footer className="border-t border-(--border-subtle) px-4 py-3">
          <button
            type="button"
            disabled={!compareWith}
            data-testid="version-compare"
            onClick={() =>
              compareWith && window.alert(`Compare v.42 ↔ ${compareWith} — disponible en B5`)
            }
            className={`flex w-full items-center justify-center gap-1 rounded-md border px-3 py-1.5 text-sm transition ${
              compareWith
                ? "border-(--accent-500) bg-(--accent-500) text-white hover:bg-(--accent-600)"
                : "border-(--border-subtle) text-(--text-tertiary) cursor-not-allowed"
            }`}
          >
            <History size={12} /> Comparar v.42 ↔ {compareWith ?? "…"}
          </button>
        </footer>
      </aside>
    </>
  );
}
